import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Search, Filter, Plus, Eye, Edit2, Trash2, Truck, Plane, Ship, Train, Package, Loader2, Printer, MessageSquare, MessageCircle, Mail, X, Send, Tag, MoreVertical, CheckSquare, Sparkles } from 'lucide-react';
import AiDocketAuditModal from '../components/AiDocketAuditModal';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { useToast } from '../hooks/use-toast';
import CreateLabelModal from '../components/CreateLabelModal';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

import Pagination from '../components/ui/pagination';

const DocketList = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [shipmentTypeFilter, setShipmentTypeFilter] = useState(searchParams.get('shipment_type') || 'all');
  const [dockets, setDockets] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [docketToDelete, setDocketToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // SMS modal state
  const [smsModal, setSmsModal] = useState({ open: false, docket: null });
  const [smsMobile, setSmsMobile] = useState('');
  const [smsSending, setSmsSending] = useState(false);

  // WhatsApp modal state
  const [waModal, setWaModal] = useState({ open: false, docket: null });
  const [waMobile, setWaMobile] = useState('');
  const [waSending, setWaSending] = useState(false);

  // Email modal state
  const [emailModal, setEmailModal] = useState({ open: false, docket: null });
  const [emailRecipients, setEmailRecipients] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [emailSending, setEmailSending] = useState(false);

  // Label modal state
  const [labelModal, setLabelModal] = useState({ open: false, docket: null });
  const [selectedAuditDocket, setSelectedAuditDocket] = useState(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('auth_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }, []);

  const fetchDockets = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (shipmentTypeFilter !== 'all') params.append('shipment_type', shipmentTypeFilter);
      if (searchQuery) params.append('search', searchQuery);
      params.append('skip', String((page - 1) * pageSize));
      params.append('limit', String(pageSize));
      
      const response = await fetch(`${backendUrl}/api/dockets?${params}`, { headers: getAuthHeaders() });
      if (!response.ok) throw new Error('Failed to fetch dockets');
      const data = await response.json();
      setDockets(data.items || []);
      setTotal(data.total || 0);
    } catch (error) {
      toast({ title: "Error", description: "Failed to fetch dockets", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [backendUrl, getAuthHeaders, statusFilter, shipmentTypeFilter, searchQuery, page, pageSize, toast]);

  useEffect(() => { fetchDockets(); }, [fetchDockets]);

  // Reset to page 1 on filter changes, then fetch
  useEffect(() => { setPage(1); }, [statusFilter, shipmentTypeFilter]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => { setPage(1); fetchDockets(); }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]); // eslint-disable-line

  const getShipmentIcon = (type) => {
    switch (type) {
      case 'ROAD': return <Truck className="w-4 h-4" />;
      case 'AIR': return <Plane className="w-4 h-4" />;
      case 'SEA': return <Ship className="w-4 h-4" />;
      case 'RAIL': return <Train className="w-4 h-4" />;
      case 'COURIER': return <Package className="w-4 h-4" />;
      default: return <Package className="w-4 h-4" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'INITIATE': return 'bg-slate-100 text-slate-700';
      case 'PICK_UP': return 'bg-purple-100 text-purple-700';
      case 'IN_TRANSIT': return 'bg-blue-100 text-blue-700';
      case 'DEPARTED': return 'bg-indigo-100 text-indigo-700';
      case 'ARRIVED': return 'bg-cyan-100 text-cyan-700';
      case 'DELIVERED': return 'bg-green-100 text-green-700';
      case 'COMPLETED': return 'bg-emerald-100 text-emerald-700';
      case 'UNDER_CLEARANCE': return 'bg-amber-100 text-amber-700';
      case 'CUSTOM_DELAY': return 'bg-orange-100 text-orange-700';
      case 'OUT_OF_DELIVERY': return 'bg-teal-100 text-teal-700';
      case 'RECEIVED_AT_ORIGIN_HUB': return 'bg-sky-100 text-sky-700';
      case 'RECEIVED_AT_DEST_HUB': return 'bg-violet-100 text-violet-700';
      case 'CLEARANCE_COMPLETED': return 'bg-lime-100 text-lime-700';
      case 'CARTING_DATE': return 'bg-fuchsia-100 text-fuchsia-700';
      case 'HO_TO_AIRLINES': return 'bg-rose-100 text-rose-700';
      case 'OTHERS': return 'bg-gray-100 text-gray-700';
      case 'CANCELLED': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const formatStatusLabel = (status) => {
    if (!status) return '-';
    return status.replace(/_/g, ' ');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const openSmsModal = (docket) => {
    setSmsModal({ open: true, docket });
    const phones = (docket?.recipient_phones && docket.recipient_phones.length > 0)
      ? docket.recipient_phones.join(', ')
      : '';
    setSmsMobile(phones);
  };

  const handleSendSms = async () => {
    if (!smsMobile.trim()) { toast({ title: 'Enter mobile number', variant: 'destructive' }); return; }
    setSmsSending(true);
    try {
      const res = await fetch(`${backendUrl}/api/sms/send`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          mobile: smsMobile.trim(),
          docket_no: smsModal.docket?.system_docket_no || '',
          customer_name: smsModal.docket?.exporter_name || smsModal.docket?.importer_name || '',
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      toast({ title: 'SMS Sent', description: `Message sent to ${smsMobile}` });
      setSmsModal({ open: false, docket: null });
    } catch (e) {
      toast({ title: 'SMS Failed', description: e.message, variant: 'destructive' });
    } finally {
      setSmsSending(false);
    }
  };

  const openWaModal = (docket) => {
    setWaModal({ open: true, docket });
    const phones = (docket?.recipient_phones && docket.recipient_phones.length > 0)
      ? docket.recipient_phones.join(', ')
      : '';
    setWaMobile(phones);
  };

  const openEmailModal = (docket) => {
    setEmailModal({ open: true, docket });
    const emails = (docket?.recipient_emails && docket.recipient_emails.length > 0)
      ? docket.recipient_emails.join(', ')
      : '';
    setEmailRecipients(emails);
    setEmailSubject(`Shipment Booked - Docket No. ${docket?.system_docket_no || ''}`);
    setEmailBody(
`Dear Customer,

Your shipment has been booked successfully with STAVAN GROUP.

Docket No: ${docket?.system_docket_no || ''}
Shipment Type: ${docket?.shipment_type || ''}
Date: ${formatDate(docket?.docket_date || docket?.created_at)}
Consignor: ${docket?.exporter_name || 'N/A'}
Consignee: ${docket?.importer_name || 'N/A'}
Status: ${formatStatusLabel(docket?.status || '')}

Track your parcel here:
https://www.stavanlogistics.com/track-your-parcel/

Thank you for choosing STAVAN GROUP.`
    );
  };

  const handleSendEmail = async () => {
    if (!emailRecipients.trim()) { toast({ title: 'Enter recipient email(s)', variant: 'destructive' }); return; }
    setEmailSending(true);
    try {
      const res = await fetch(`${backendUrl}/api/emails/send`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          to_email: emailRecipients.trim(),
          subject: emailSubject.trim() || `Shipment Booked - Docket No. ${emailModal.docket?.system_docket_no || ''}`,
          html_body: emailBody.trim(),
          docket_id: emailModal.docket?.id,
          company_id: emailModal.docket?.company_id,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || data.message || 'Failed');
      toast({ title: 'Email Sent', description: data.message || `Email sent to ${emailRecipients}` });
      setEmailModal({ open: false, docket: null });
    } catch (e) {
      toast({ title: 'Email Failed', description: e.message, variant: 'destructive' });
    } finally {
      setEmailSending(false);
    }
  };

  const handleSendWa = async () => {
    if (!waMobile.trim()) { toast({ title: 'Enter mobile number', variant: 'destructive' }); return; }
    setWaSending(true);
    try {
      const res = await fetch(`${backendUrl}/api/whatsapp/send`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          mobile: waMobile.trim(),
          docket_no: waModal.docket?.system_docket_no || '',
          customer_name: waModal.docket?.exporter_name || waModal.docket?.importer_name || '',
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      toast({ title: 'WhatsApp Sent', description: `Message sent to ${waMobile}` });
      setWaModal({ open: false, docket: null });
    } catch (e) {
      toast({ title: 'WhatsApp Failed', description: e.message, variant: 'destructive' });
    } finally {
      setWaSending(false);
    }
  };

  const handleDeleteClick = (docket) => {
    if (docket.status !== 'INITIATE') {
      toast({
        title: "Cannot Delete",
        description: "Only INITIATE dockets can be deleted",
        variant: "destructive"
      });
      return;
    }
    setDocketToDelete(docket);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!docketToDelete) return;
    
    try {
      setDeleting(true);
      const response = await fetch(`${backendUrl}/api/dockets/${docketToDelete.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete docket');
      }
      
      toast({
        title: "Success",
        description: `Docket ${docketToDelete.system_docket_no} deleted successfully`
      });
      
      fetchDockets();
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setDocketToDelete(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-text-dark font-manrope">Dockets</h1>
          <p className="text-text-secondary font-ibm mt-1">Manage shipment dockets</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            type="button"
            onClick={() => window.dispatchEvent(new CustomEvent('open-ai-docket-assistant'))}
            className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-ibm font-semibold h-11 px-5 rounded-lg shadow-sm transition-transform active:scale-95"
            title="Create Docket from Email Screenshot with GPT-4o Vision"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            AI Docket Assistant (Option B)
          </Button>
          <Button
            data-testid="create-docket-button"
            onClick={() => navigate('/dockets/create')}
            className="bg-accent-green hover:bg-accent-green/90 text-white font-ibm font-semibold h-11 px-6 rounded-lg"
          >
            <Plus className="w-5 h-5 mr-2" />
            Create Docket
          </Button>
        </div>
      </div>

      {/* Filters Card */}
      <Card className="bg-primary-blue text-white rounded-xl shadow-lg">
        <div className="p-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <h3 className="text-xl font-semibold font-manrope">
              All Dockets ({total})
            </h3>
            
            <div className="flex items-center gap-4 flex-wrap">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/60" />
                <Input
                  data-testid="docket-search-input"
                  placeholder="Search by docket no..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 w-56 bg-white/10 border-white/20 text-white placeholder:text-white/60 focus:bg-white/20 focus:border-white/40 font-ibm"
                />
              </div>

              {/* Shipment Type Filter */}
              <Select value={shipmentTypeFilter} onValueChange={setShipmentTypeFilter}>
                <SelectTrigger 
                  data-testid="shipment-type-filter"
                  className="w-36 bg-white/10 border-white/20 text-white focus:bg-white/20 focus:border-white/40 font-ibm"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="ROAD">Road</SelectItem>
                  <SelectItem value="RAIL">Rail</SelectItem>
                  <SelectItem value="AIR">Air</SelectItem>
                  <SelectItem value="SEA">Sea</SelectItem>
                  <SelectItem value="COURIER">Courier</SelectItem>
                </SelectContent>
              </Select>

              {/* Status Filter */}
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger 
                  data-testid="status-filter"
                  className="w-44 bg-white/10 border-white/20 text-white focus:bg-white/20 focus:border-white/40 font-ibm"
                >
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="INITIATE">Initiate</SelectItem>
                  <SelectItem value="PICK_UP">Pick Up</SelectItem>
                  <SelectItem value="IN_TRANSIT">In Transit</SelectItem>
                  <SelectItem value="DEPARTED">Departed</SelectItem>
                  <SelectItem value="ARRIVED">Arrived</SelectItem>
                  <SelectItem value="DELIVERED">Delivered</SelectItem>
                  <SelectItem value="COMPLETED">Completed</SelectItem>
                  <SelectItem value="UNDER_CLEARANCE">Under Clearance</SelectItem>
                  <SelectItem value="CUSTOM_DELAY">Custom Delay</SelectItem>
                  <SelectItem value="OUT_OF_DELIVERY">Out of Delivery</SelectItem>
                  <SelectItem value="RECEIVED_AT_ORIGIN_HUB">Received at Origin Hub</SelectItem>
                  <SelectItem value="RECEIVED_AT_DEST_HUB">Received at Dest Hub</SelectItem>
                  <SelectItem value="CLEARANCE_COMPLETED">Clearance Completed</SelectItem>
                  <SelectItem value="CARTING_DATE">Carting Date</SelectItem>
                  <SelectItem value="HO_TO_AIRLINES">H/O To Airlines</SelectItem>
                  <SelectItem value="OTHERS">Others</SelectItem>
                  <SelectItem value="CANCELLED">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </Card>

      {/* Dockets Table */}
      <Card className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-accent-blue" />
            <span className="ml-2 text-text-secondary font-ibm">Loading dockets...</span>
          </div>
        ) : dockets.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Package className="w-16 h-16 text-gray-300 mb-4" />
            <p className="text-text-secondary font-ibm mb-4">No dockets found</p>
            <Button
              onClick={() => navigate('/dockets/create')}
              className="bg-accent-green hover:bg-accent-green/90 text-white font-ibm"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create First Docket
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50/50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-4 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm">Docket No</th>
                  <th className="px-4 py-4 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm">Type</th>
                  <th className="px-4 py-4 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm">Docket Date</th>
                  <th className="px-4 py-4 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm">Consignor</th>
                  <th className="px-4 py-4 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm">Consignee</th>
                  <th className="px-4 py-4 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm">Created By</th>
                  <th className="px-4 py-4 text-center text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm">Status</th>
                  <th className="px-4 py-4 text-center text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {dockets.map((docket) => (
                  <tr key={docket.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-4">
                      <div className="flex flex-col">
                        <button
                          type="button"
                          onClick={() => navigate(`/dockets/view/${docket.id}`)}
                          className="text-left text-sm font-semibold text-accent-blue hover:text-blue-700 hover:underline font-mono transition-colors focus:outline-none"
                          title="View Docket Details"
                        >
                          {docket.system_docket_no}
                        </button>
                        <span className="text-xs text-text-secondary font-ibm">{docket.company_name || '-'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        {getShipmentIcon(docket.shipment_type)}
                        <span className="text-sm text-text-dark font-ibm">{docket.shipment_type}</span>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <span className="text-sm text-text-dark font-ibm">{formatDate(docket.docket_date || docket.created_at)}</span>
                    </td>
                    <td className="px-4 py-4">
                      <span className="text-sm text-text-dark font-ibm truncate max-w-[150px] block" title={docket.exporter_name || '-'}>
                        {docket.exporter_name || '-'}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <span className="text-sm text-text-dark font-ibm truncate max-w-[150px] block" title={docket.importer_name || '-'}>
                        {docket.importer_name || '-'}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <span className="text-sm text-text-secondary font-ibm">{docket.created_by_name || '—'}</span>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <Badge className={`${getStatusColor(docket.status)} font-ibm font-semibold border-0 text-xs`}>
                        {formatStatusLabel(docket.status)}
                      </Badge>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center justify-center gap-1">
                        {/* Quick Action 1: Edit */}
                        <button
                          data-testid={`edit-docket-${docket.id}`}
                          onClick={() => navigate(`/dockets/edit/${docket.id}`)}
                          className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors text-text-secondary hover:text-text-dark"
                          title="Edit Docket"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>

                        {/* Quick Action 2: Print */}
                        <button
                          data-testid={`print-docket-${docket.id}`}
                          onClick={() => navigate(`/dockets/print/${docket.id}`)}
                          className="w-8 h-8 flex items-center justify-center hover:bg-blue-50 rounded-lg transition-colors text-blue-600"
                          title="Print Docket"
                        >
                          <Printer className="w-4 h-4" />
                        </button>

                        {/* Quick Action 3: AI Audit & Copilot */}
                        <button
                          data-testid={`ai-audit-${docket.id}`}
                          onClick={() => setSelectedAuditDocket(docket)}
                          className="w-8 h-8 flex items-center justify-center hover:bg-purple-50 rounded-lg transition-colors text-purple-600"
                          title="AI Compliance Audit & Copilot"
                        >
                          <Sparkles className="w-4 h-4" />
                        </button>

                        {/* Dropdown for More Actions */}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              data-testid={`more-actions-${docket.id}`}
                              className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors text-text-secondary hover:text-text-dark"
                              title="More Actions"
                            >
                              <MoreVertical className="w-4 h-4" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-44 bg-white shadow-lg border border-gray-100 rounded-lg p-1 z-50">
                            <DropdownMenuItem
                              onClick={() => navigate(`/dockets/view/${docket.id}`)}
                              className="cursor-pointer text-xs flex items-center py-2 px-3 hover:bg-gray-50 rounded text-gray-700"
                            >
                              <Eye className="w-4 h-4 mr-2 text-gray-500" />
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => setLabelModal({ open: true, docket })}
                              className="cursor-pointer text-xs flex items-center py-2 px-3 hover:bg-gray-50 rounded text-gray-700"
                            >
                              <Tag className="w-4 h-4 mr-2 text-emerald-600" />
                              Print Label
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => navigate(`/tasks?search=${encodeURIComponent(docket.system_docket_no)}`)}
                              className="cursor-pointer text-xs flex items-center py-2 px-3 hover:bg-gray-50 rounded text-gray-700"
                            >
                              <CheckSquare className="w-4 h-4 mr-2 text-blue-600" />
                              Tasks (TMS)
                            </DropdownMenuItem>
                            <DropdownMenuSeparator className="my-1 bg-gray-100" />
                            <DropdownMenuItem
                              onClick={() => openWaModal(docket)}
                              className="cursor-pointer text-xs flex items-center py-2 px-3 hover:bg-gray-50 rounded text-gray-700"
                            >
                              <MessageCircle className="w-4 h-4 mr-2 text-emerald-600" />
                              WhatsApp
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => openEmailModal(docket)}
                              className="cursor-pointer text-xs flex items-center py-2 px-3 hover:bg-gray-50 rounded text-gray-700"
                            >
                              <Mail className="w-4 h-4 mr-2 text-sky-600" />
                              Email
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => openSmsModal(docket)}
                              className="cursor-pointer text-xs flex items-center py-2 px-3 hover:bg-gray-50 rounded text-gray-700"
                            >
                              <MessageSquare className="w-4 h-4 mr-2 text-green-600" />
                              SMS
                            </DropdownMenuItem>
                            <DropdownMenuSeparator className="my-1 bg-gray-100" />
                            <DropdownMenuItem
                              onClick={() => docket.status === 'INITIATE' && handleDeleteClick(docket)}
                              disabled={docket.status !== 'INITIATE'}
                              className={`cursor-pointer text-xs flex items-center py-2 px-3 rounded ${
                                docket.status === 'INITIATE'
                                  ? 'hover:bg-red-50 text-red-600 focus:text-red-600 focus:bg-red-50'
                                  : 'text-gray-300 cursor-not-allowed'
                              }`}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete Docket
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading && dockets.length > 0 && (
          <Pagination total={total} page={page} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={v => { setPageSize(v); setPage(1); }} />
        )}
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Docket</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete docket <strong>{docketToDelete?.system_docket_no}</strong>? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteConfirm}
              disabled={deleting}
              className="bg-red-500 hover:bg-red-600"
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* SMS Modal */}
      {smsModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6" data-testid="sms-modal">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-green-600" />
                <h3 className="font-bold text-text-dark font-manrope text-lg">Send SMS</h3>
              </div>
              <button onClick={() => setSmsModal({ open: false, docket: null })}
                className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg">
                <X className="w-4 h-4 text-text-secondary" />
              </button>
            </div>

            <div className="bg-gray-50 rounded-xl p-3 mb-4">
              <p className="text-xs text-text-secondary font-ibm">Docket</p>
              <p className="font-semibold text-text-dark font-ibm">{smsModal.docket?.system_docket_no}</p>
              {smsModal.docket?.exporter_name && (
                <p className="text-xs text-text-secondary font-ibm mt-0.5">Exporter: {smsModal.docket.exporter_name}</p>
              )}
            </div>

            <div className="space-y-2 mb-5">
              <label className="text-sm font-medium text-text-dark font-ibm block">
                Mobile Number <span className="text-text-secondary font-normal">(recipient)</span>
              </label>
              <input
                data-testid="sms-modal-mobile"
                type="tel"
                autoFocus
                placeholder="Enter mobile numbers (comma-separated, e.g. 9876543210, 9123456780)"
                value={smsMobile}
                onChange={e => setSmsMobile(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSendSms()}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg font-ibm text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
              />
              <p className="text-xs text-text-secondary font-ibm">Auto-filled from docket parties. You can edit or add numbers separated by comma.</p>
            </div>

            <div className="flex gap-3">
              <button onClick={() => setSmsModal({ open: false, docket: null })}
                className="flex-1 py-2.5 px-4 border border-gray-200 rounded-xl font-ibm text-sm text-text-secondary hover:bg-gray-50 transition-colors">
                Cancel
              </button>
              <button
                data-testid="sms-modal-send-btn"
                onClick={handleSendSms}
                disabled={smsSending || !smsMobile.trim()}
                className="flex-1 py-2.5 px-4 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded-xl font-ibm text-sm font-semibold flex items-center justify-center gap-2 transition-colors"
              >
                {smsSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {smsSending ? 'Sending...' : 'Send SMS'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* WhatsApp Modal */}
      {waModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6" data-testid="wa-modal">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-emerald-600" />
                <h3 className="font-bold text-text-dark font-manrope text-lg">Send WhatsApp</h3>
              </div>
              <button onClick={() => setWaModal({ open: false, docket: null })}
                className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg">
                <X className="w-4 h-4 text-text-secondary" />
              </button>
            </div>

            <div className="bg-emerald-50 rounded-xl p-3 mb-4 border border-emerald-100">
              <p className="text-xs text-emerald-700 font-ibm">Docket</p>
              <p className="font-semibold text-text-dark font-ibm">{waModal.docket?.system_docket_no}</p>
              {waModal.docket?.exporter_name && (
                <p className="text-xs text-text-secondary font-ibm mt-0.5">Exporter: {waModal.docket.exporter_name}</p>
              )}
              {waModal.docket?.importer_name && (
                <p className="text-xs text-text-secondary font-ibm mt-0.5">Importer: {waModal.docket.importer_name}</p>
              )}
            </div>

            <div className="space-y-2 mb-5">
              <label className="text-sm font-medium text-text-dark font-ibm block">
                Mobile Number <span className="text-text-secondary font-normal">(recipient)</span>
              </label>
              <input
                data-testid="wa-modal-mobile"
                type="tel"
                autoFocus
                placeholder="Enter mobile numbers (comma-separated, e.g. 9876543210, 9123456780)"
                value={waMobile}
                onChange={e => setWaMobile(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSendWa()}
                className="w-full px-3 py-2 border border-emerald-200 rounded-lg font-ibm text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
              />
              <p className="text-xs text-text-secondary font-ibm">
                Auto-filled from docket parties. You can edit or add numbers separated by comma (+91 prefix auto-added).
              </p>
            </div>

            <div className="flex gap-3">
              <button onClick={() => setWaModal({ open: false, docket: null })}
                className="flex-1 py-2.5 px-4 border border-gray-200 rounded-xl font-ibm text-sm text-text-secondary hover:bg-gray-50 transition-colors">
                Cancel
              </button>
              <button
                data-testid="wa-modal-send-btn"
                onClick={handleSendWa}
                disabled={waSending || !waMobile.trim()}
                className="flex-1 py-2.5 px-4 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-xl font-ibm text-sm font-semibold flex items-center justify-center gap-2 transition-colors"
              >
                {waSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {waSending ? 'Sending...' : 'Send WhatsApp'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email Modal */}
      {emailModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 p-6" data-testid="email-modal">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-sky-600" />
                <h3 className="font-bold text-text-dark font-manrope text-lg">Send Email</h3>
              </div>
              <button onClick={() => setEmailModal({ open: false, docket: null })}
                className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg">
                <X className="w-4 h-4 text-text-secondary" />
              </button>
            </div>

            <div className="bg-sky-50 rounded-xl p-3 mb-4 border border-sky-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-sky-700 font-ibm">Docket</p>
                  <p className="font-semibold text-text-dark font-ibm">{emailModal.docket?.system_docket_no}</p>
                </div>
                <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-sky-100 text-sky-800 border border-sky-200">
                  Logo &amp; Company Footer Included
                </span>
              </div>
              {emailModal.docket?.exporter_name && (
                <p className="text-xs text-text-secondary font-ibm mt-0.5">Exporter: {emailModal.docket.exporter_name}</p>
              )}
              {emailModal.docket?.importer_name && (
                <p className="text-xs text-text-secondary font-ibm mt-0.5">Importer: {emailModal.docket.importer_name}</p>
              )}
            </div>

            <div className="space-y-4 mb-5">
              <div>
                <label className="text-sm font-medium text-text-dark font-ibm block mb-1">
                  Recipient Email(s) <span className="text-text-secondary font-normal">(comma-separated)</span>
                </label>
                <input
                  data-testid="email-modal-recipients"
                  type="text"
                  autoFocus
                  placeholder="Enter recipient email(s) (e.g. user@example.com, client@example.com)"
                  value={emailRecipients}
                  onChange={e => setEmailRecipients(e.target.value)}
                  className="w-full px-3 py-2 border border-sky-200 rounded-lg font-ibm text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
                />
                <p className="text-xs text-text-secondary font-ibm mt-1">
                  Auto-filled from docket parties. You can edit or add emails separated by comma.
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-text-dark font-ibm block mb-1">
                  Subject
                </label>
                <input
                  data-testid="email-modal-subject"
                  type="text"
                  value={emailSubject}
                  onChange={e => setEmailSubject(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg font-ibm text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-text-dark font-ibm block mb-1">
                  Message Body <span className="text-xs text-text-secondary font-normal">(Logo &amp; Footer auto-attached)</span>
                </label>
                <textarea
                  data-testid="email-modal-body"
                  rows={6}
                  value={emailBody}
                  onChange={e => setEmailBody(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg font-ibm text-xs font-mono focus:outline-none focus:ring-2 focus:ring-sky-400"
                />
              </div>
            </div>

            <div className="flex gap-3">
              <button onClick={() => setEmailModal({ open: false, docket: null })}
                className="flex-1 py-2.5 px-4 border border-gray-200 rounded-xl font-ibm text-sm text-text-secondary hover:bg-gray-50 transition-colors">
                Cancel
              </button>
              <button
                data-testid="email-modal-send-btn"
                onClick={handleSendEmail}
                disabled={emailSending || !emailRecipients.trim()}
                className="flex-1 py-2.5 px-4 bg-sky-600 hover:bg-sky-700 disabled:opacity-50 text-white rounded-xl font-ibm text-sm font-semibold flex items-center justify-center gap-2 transition-colors"
              >
                {emailSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {emailSending ? 'Sending...' : 'Send Email'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CREATE LABEL MODAL */}
      <CreateLabelModal
        isOpen={labelModal.open}
        onClose={() => setLabelModal({ open: false, docket: null })}
        docket={labelModal.docket}
        onPrint={(labelForm) => {
          const docId = labelModal.docket?.id;
          setLabelModal({ open: false, docket: null });
          if (docId) {
            const params = new URLSearchParams({
              from: labelForm.fromLocation,
              to: labelForm.toLocation,
              packages: labelForm.packages,
              size: labelForm.printSize,
              additional: labelForm.additionalInfo
            });
            window.open(`/dockets/print-label/${docId}?${params.toString()}`, '_blank');
          }
        }}
      />

      <AiDocketAuditModal
        isOpen={!!selectedAuditDocket}
        docket={selectedAuditDocket}
        onClose={() => setSelectedAuditDocket(null)}
      />
    </div>
  );
};

export default DocketList;
